
import { useState, useEffect } from 'react';
import { supabase, type Game } from '../../../lib/supabase';
import { useSiteLogo } from '../../../hooks/useSiteLogo';
import NewsSection from './NewsSection';

const buildLocalDateTime = (dateStr?: string | null, timeStr?: string | null) => {
  if (!dateStr) return null;
  const [year, month, day] = dateStr.split('-').map(Number);
  if (
    Number.isNaN(year) ||
    Number.isNaN(month) ||
    Number.isNaN(day)
  ) {
    return null;
  }

  const [hours, minutes] = (timeStr || '00:00').split(':').map(Number);
  const safeHours = Number.isNaN(hours) ? 0 : hours;
  const safeMinutes = Number.isNaN(minutes) ? 0 : minutes;

  return new Date(year, month - 1, day, safeHours, safeMinutes, 0, 0);
};

const HeroSection = () => {
  const [upcomingGames, setUpcomingGames] = useState<Game[]>([]);
  const [loading, setLoading] = useState(true);
  const { logo, loading: logoLoading } = useSiteLogo();

  useEffect(() => {
    fetchUpcomingGames();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUpcomingGames = async () => {
    try {
      setLoading(true);

      // Prepare date range: today (midnight) -> next 7 days
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayStr = today.toISOString().split('T')[0];

      const nextWeek = new Date(today);
      nextWeek.setDate(today.getDate() + 7);
      const nextWeekStr = nextWeek.toISOString().split('T')[0];

      const nextWeekEnd = new Date(today);
      nextWeekEnd.setDate(today.getDate() + 7);
      nextWeekEnd.setHours(23, 59, 59, 999);

      const { data, error } = await supabase
        .from('games')
        .select('*')
        .gte('date', todayStr)
        .lte('date', nextWeekStr)
        .order('date', { ascending: true });

      if (error) {
        console.error('Error fetching games:', error);
        setUpcomingGames([]);
        return;
      }

      const now = new Date();
      const filteredGames = (data ?? [])
        .filter((game) => {
          const gameDateTime = buildLocalDateTime(game.date, game.time);
          if (!gameDateTime) return false;
          if (gameDateTime < now) return false;
          if (gameDateTime > nextWeekEnd) return false;
          return true;
        })
        .sort((a, b) => {
          const dateA = buildLocalDateTime(a.date, a.time)?.getTime() ?? 0;
          const dateB = buildLocalDateTime(b.date, b.time)?.getTime() ?? 0;
          return dateA - dateB;
        });

      setUpcomingGames(filteredGames);
    } catch (err) {
      console.error('Unexpected error while fetching games:', err);
      setUpcomingGames([]);
    } finally {
      setLoading(false);
    }
  };

  const renderLogo = () => {
    if (logoLoading) {
      return <div className="w-28 h-28 lg:w-40 lg:h-40 bg-gray-300 animate-pulse rounded"></div>;
    }

    // Usar siempre la imagen del bucket de Supabase
    const logoUrl = 'https://yfsgqrzophdjgqardzvy.supabase.co/storage/v1/object/public/media/logo_white.png';

    return (
      <img
        src={logoUrl}
        alt="Viubàsquet Logo"
        className="w-28 h-28 lg:w-40 lg:h-40 object-contain"
        onError={(e) => {
          const target = e.target as HTMLImageElement;
          target.style.display = 'none';
          const parent = target.parentElement;
          if (parent) {
            parent.innerHTML = '<span class="text-2xl lg:text-4xl font-bold text-gray-800" style="font-family: \\"Pacifico\\", serif;">logo</span>';
          }
        }}
      />
    );
  };

  return (
    <section className="bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white min-h-screen">
      {/* Header con logo */}
      <div className="px-4 lg:px-8 pt-6 pb-4 text-center">
        {/* <div className="text-center mb-8 lg:mb-12">
          <div className="w-28 h-28 lg:w-40 lg:h-40 mx-auto mb-3 lg:mb-6 flex items-center justify-center">
            <img 
              src="https://yfsgqrzophdjgqardzvy.supabase.co/storage/v1/object/public/media/logo_white.png" 
              alt="Viubàsquet Logo"
              className="w-24 h-24 lg:w-36 lg:h-36 object-contain"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
                const parent = target.parentElement;
                if (parent) {
                  parent.innerHTML = '<span class="text-xl lg:text-3xl font-bold text-white" style="font-family: \\"Pacifico\\", serif;">logo</span>';
                }
              }}
            />
          </div>
          <h1 className="text-2xl lg:text-4xl font-bold mb-1 lg:mb-2">Viubàsquet</h1>
          <p className="text-sm lg:text-lg text-white/70">Club de Baloncesto</p>
        </div> */}
      </div>

      {/* Contenedor principal responsive */}
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
          
          {/* Próximos Partidos */}
          <div className="pb-6">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 lg:p-6 border border-white/20">
              <h3 className="text-base lg:text-xl font-semibold mb-3 lg:mb-4 flex items-center">
                <i className="ri-calendar-line mr-2"></i>
                Próximos Partidos (7 días)
              </h3>

              {loading ? (
                <div className="text-center py-8 lg:py-12">
                  <div className="inline-block w-8 h-8 lg:w-12 lg:h-12 border-3 border-white/30 border-t-white rounded-full animate-spin"></div>
                </div>
              ) : upcomingGames.length === 0 ? (
                <div className="text-center py-8 lg:py-12">
                  <i className="ri-calendar-close-line text-4xl lg:text-6xl text-white/40 mb-2 lg:mb-4"></i>
                  <p className="text-sm lg:text-base text-white/60">No hay partidos próximos</p>
                </div>
              ) : (
                <div className="space-y-3 lg:space-y-4">
                  {upcomingGames.map((game) => (
                    <div
                      key={game.id}
                      className="bg-gradient-to-br from-gray-800 via-gray-700 to-gray-800 rounded-2xl p-4 lg:p-6 shadow-xl border border-gray-600/50 backdrop-blur-sm"
                    >
                      <div className="flex items-center justify-between mb-3 lg:mb-4">
                        <div className="bg-white/10 backdrop-blur-sm px-2.5 py-1 lg:px-3 lg:py-1.5 rounded-full border border-white/20">
                          <span className="text-xs lg:text-sm font-medium text-white">
                            {new Date(game.date + 'T00:00:00').toLocaleDateString(
                              'es-ES',
                              {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                              }
                            )}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          {game.jornada && (
                            <div className="bg-pink-500/80 backdrop-blur-sm px-2.5 py-1 lg:px-3 lg:py-1.5 rounded-full border border-pink-400/30">
                              <span className="text-xs lg:text-sm font-medium text-white">
                                Jornada {game.jornada}
                              </span>
                            </div>
                          )}
                          <div className="bg-white/10 backdrop-blur-sm px-2.5 py-1 lg:px-3 lg:py-1.5 rounded-full border border-white/20">
                            <span className="text-xs lg:text-sm font-medium text-white flex items-center">
                              <i className="ri-time-line mr-1"></i>
                              {game.time}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center justify-center gap-3 lg:gap-6 my-4 lg:my-6">
                        <div className="flex-1 text-center">
                          <div className="w-16 h-16 lg:w-20 lg:h-20 mx-auto mb-2 lg:mb-3 rounded-xl flex items-center justify-center">
                            {game.home_team_logo ? (
                              <img
                                src={game.home_team_logo}
                                alt={game.home_team ?? 'Home team'}
                                className="w-14 h-14 lg:w-18 lg:h-18 object-contain"
                              />
                            ) : (
                              <img
                                src={`https://yfsgqrzophdjgqardzvy.supabase.co/storage/v1/object/public/media/${game.home_team?.toLowerCase().replace(/\\s+/g, '_').replace(/[^a-z0-9_]/g, '')}.png`}
                                alt={game.home_team ?? 'Home team'}
                                className="w-14 h-14 lg:w-18 lg:h-18 object-contain"
                                onError={(e) => {
                                  const target = e.target as HTMLImageElement;
                                  target.src = 'https://readdy.ai/api/search-image?query=Basketball%20team%20logo%2C%20circular%20emblem%2C%20orange%20and%20white%20colors%2C%20modern%20design%2C%20sports%20logo%2C%20clean%20background%2C%20professional%20basketball%20club%20badge&width=60&height=60&seq=home-logo&orientation=squarish';
                                }}
                              />
                            )}
                          </div>
                          <p className="text-xs lg:text-sm font-semibold text-white line-clamp-2">
                            {game.home_team || game.title}
                          </p>
                        </div>
                        <div className="w-12 h-12 lg:w-16 lg:h-16 flex items-center justify-center flex-shrink-0">
                          <span className="text-lg lg:text-2xl font-bold text-white">VS</span>
                        </div>
                        <div className="flex-1 text-center">
                          <div className="w-16 h-16 lg:w-20 lg:h-20 mx-auto mb-2 lg:mb-3 rounded-xl flex items-center justify-center">
                            {game.away_team_logo ? (
                              <img
                                src={game.away_team_logo}
                                alt={game.away_team ?? 'Away team'}
                                className="w-14 h-14 lg:w-18 lg:h-18 object-contain"
                              />
                            ) : (
                              <img
                                src={`https://yfsgqrzophdjgqardzvy.supabase.co/storage/v1/object/public/media/${game.away_team?.toLowerCase().replace(/\\s+/g, '_').replace(/[^a-z0-9_]/g, '')}.png`}
                                alt={game.away_team ?? 'Away team'}
                                className="w-14 h-14 lg:w-18 lg:h-18 object-contain"
                                onError={(e) => {
                                  const target = e.target as HTMLImageElement;
                                  target.src = 'https://readdy.ai/api/search-image?query=Basketball%20team%20logo%2C%20circular%20emblem%2C%20blue%20and%20white%20colors%2C%20modern%20design%2C%20sports%20logo%2C%20clean%20background%2C%20professional%20basketball%20club%20badge&width=60&height=60&seq=away-logo&orientation=squarish';
                                }}
                              />
                            )}
                          </div>
                          <p className="text-xs lg:text-sm font-semibold text-white line-clamp-2">
                            {game.away_team}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center justify-center text-xs lg:text-sm text-white/70 bg-white/5 backdrop-blur-sm rounded-lg py-1.5 px-2 lg:py-2 lg:px-3 mt-3 lg:mt-4">
                        <i className="ri-map-pin-line mr-1 text-xs lg:text-sm"></i>
                        <span>{game.location || 'Ubicación por confirmar'}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Sección de noticias en desktop */}
          <div className="hidden lg:block">
            <NewsSection />
          </div>
        </div>

        {/* Sección de noticias en móvil */}
        <div className="lg:hidden">
          <NewsSection />
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
